/**
 * How much of the picture is allowed to move on its own, for every mode at once.
 *
 * One switch over the rest, which is the shape the problem actually has. A player who cannot take
 * a moving picture wants one control, not a hunt through a list; a player who only cannot take the
 * bob wants the bob. So the master answers for every effect and each answers for itself, and
 * `motionEffectOn` is the one question anything downstream asks. Nothing reads the two halves and
 * decides for itself, because two readers deciding differently is a bob that is off in the camera
 * and on in the viewmodel.
 *
 * The master seeds from `prefers-reduced-motion` through `DEFAULT_MOTION_REDUCED`, which is the
 * knob's default rather than a value written into a save. A player who has never opened the
 * settings screen gets what they already told their operating system, and the moment they touch the
 * knob their answer is stored and wins. That is why the seed is a default and not a first-run
 * write: a stored copy would freeze whatever the system said on the day the profile was made.
 *
 * The listeners are for a reader that is built rather than asked. The bob and the shake read per
 * frame and pick a change up on the next one; anything constructed to the answer has to be told
 * instead. A subscription rather than polling, because rebuilding is what the answer changing
 * means and asking sixty times a second to hear it once is waste.
 */

import { MOTION_EFFECT_ORDER } from '@/core/motion/motionContract';
import type { MotionEffectId } from '@/core/motion/motionContract';
import { prefersReducedMotion } from '@/core/motion/prefersReducedMotion';

/** What the system setting says, read once per session, and only ever a default. */
export const DEFAULT_MOTION_REDUCED = prefersReducedMotion();

/** Every effect is on under a master that is off. The master is what knows better. */
export const DEFAULT_MOTION_EFFECT = true;

let reduced = DEFAULT_MOTION_REDUCED;
const effects = new Map<MotionEffectId, boolean>(
  MOTION_EFFECT_ORDER.map((id) => [id, DEFAULT_MOTION_EFFECT]),
);

const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

/** Whether the master switch is down. Read by the knobs that sit under it, and by nothing else. */
export function motionReduced(): boolean {
  return reduced;
}

export function setMotionReduced(next: boolean): void {
  if (next === reduced) return;
  reduced = next;
  announce();
}

/** Whether this one effect's own switch is up, ignoring the master. What its own knob draws. */
export function motionEffectChosen(id: MotionEffectId): boolean {
  return effects.get(id) ?? DEFAULT_MOTION_EFFECT;
}

export function setMotionEffect(id: MotionEffectId, on: boolean): void {
  if (motionEffectChosen(id) === on) return;
  effects.set(id, on);
  announce();
}

/**
 * Whether to draw this motion at all: the master says no, or this effect's own switch does.
 *
 * The one question every reader asks. The master wins because it is the thing a player reaches for
 * when they do not yet know which effect is the one making them ill.
 */
export function motionEffectOn(id: MotionEffectId): boolean {
  return !reduced && motionEffectChosen(id);
}

/** Runs `listener` whenever any of the answers above changes. Returns the way to stop. */
export function onMotionPreferenceChange(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}
